
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { z } from "zod";
import { AlertCircle } from "lucide-react";

interface SignUpFormProps {
  onToggleMode: () => void;
}

// Validation schema
const signUpSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export function SignUpForm({ onToggleMode }: SignUpFormProps) {
  const { isLoading, handleSignUp } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateField = (field: keyof typeof formData, value: string) => {
    try {
      const fieldSchema = z.object({ [field]: signUpSchema.shape[field] });
      fieldSchema.parse({ [field]: value });
      setErrors(prev => ({ ...prev, [field]: "" }));
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldError = error.errors.find(e => e.path[0] === field)?.message || "";
        setErrors(prev => ({ ...prev, [field]: fieldError }));
        return false;
      }
      return true;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    validateField(id as keyof typeof formData, value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    let isValid = true;
    Object.entries(formData).forEach(([field, value]) => {
      if (!validateField(field as keyof typeof formData, value)) {
        isValid = false;
      }
    });

    if (isValid) {
      handleSignUp(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-white">
          Email
        </Label>
        <Input
          id="email"
          type="email"
          placeholder="m@example.com"
          value={formData.email}
          onChange={handleChange}
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
        {errors.email && (
          <div className="mt-1 text-sm font-medium bg-destructive/10 p-2 rounded-md border border-destructive/20 text-black">
            <span className="flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.email}
            </span>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="password" className="text-white">
          Password
        </Label>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          value={formData.password}
          onChange={handleChange}
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
        {errors.password && (
          <div className="mt-1 text-sm font-medium bg-destructive/10 p-2 rounded-md border border-destructive/20 text-black">
            <span className="flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.password}
            </span>
          </div>
        )}
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading || Object.values(errors).some(error => error !== "")}
      >
        {isLoading ? "Loading..." : "Sign Up"}
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="w-full text-white hover:text-white hover:bg-white/10"
        onClick={onToggleMode}
      >
        Already have an account? Sign in
      </Button>
    </form>
  );
}
